/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import BookingAction from "store/booking/BookingAction";
import {Company} from "types/entities/Company";
import AlertConfirmation from "pages/Front/Registration/components/AlertConfirmation";
import {toast} from "react-toastify";
import {useTypedSelector} from "helpers/reducers";
import DatePicker, {registerLocale, setDefaultLocale} from "react-datepicker";
import Dates from "utils/Dates";
import {declareLocaleByConfig} from "utils/localeUtils";
import {DEFAULT_HALL_ID, LOCALE} from "helpers/config";
import SellerTimeslots from "pages/Front/Booking/Seller/SellerTimeslots";
import {Timeslot} from "types/entities/Timeslot";
import {PackageCategory} from "types/enums/PackageCategory";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import {CompanyItem} from "pages/Front/Booking/Seller/components/CompanyItem";
import SellerPersonsCount from "pages/Front/Booking/Seller/components/SellerPersonsCount";
import SellerUpsells from "pages/Front/Booking/Seller/SellerUpsells";
import {consoleLog} from "utils/utility";
import SellerCompanyForm from "pages/Front/Booking/Seller/components/SellerCompanyForm";
import {BuildingOfficeIcon, EnvelopeIcon, MapIcon, MapPinIcon, UserIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import SellerButtonSwitch from "pages/Front/Booking/Seller/components/SellerButtonSwitch";
import SellerFooter from "pages/Front/Booking/Seller/SellerFooter";
import SellerHeader from "pages/Front/Booking/Seller/SellerHeader";
import SellerBookingRecapitulation from "pages/Front/Booking/Seller/components/SellerBookingRecapitulation";
import ReservationsForDate from "pages/Front/Booking/Seller/ReservationsForDate";
import DatePickerCustomHeader
    from "pages/Front/Booking/components/BookingFilters/BookingDateFilterInline/DatePickerCustomHeader";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import Table from "pages/Front/components/Table/Table";
import TableHead from "pages/Front/components/Table/TableHead";
import TableHeadColumn from "pages/Front/components/Table/TableHeadColumn";
import TableHeadTitle from "pages/Front/components/Table/TableHeadTitle";
import TableBody from "pages/Front/components/Table/TableBody";
import TableRow from "pages/Front/components/Table/TableRow";
import SellerPackageItem from "pages/Front/Booking/Seller/components/PackageItem";
import {useDebouncedCallback} from "use-debounce";
import SellerAction from "store/seller/SellerAction";
import {useHistory} from "react-router";
import dayjs from "dayjs";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";

interface DriverModalProps {
    open: boolean;
    driver?: null | ReservationDriver;
}

interface Params {
    reservation_id: string;
}

interface BookingSellerReservationDetailProps extends RouteComponentProps<Params> {
}

interface EventPackage {
    label: string;
    value: PackageCategory;
    description: string;
    color: string;
    shortLabel: string;
    link: string;
}

const SellerNewReservation = (props: BookingSellerReservationDetailProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const hallOpeningHours = useHallOpeningHours();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [textEntered, setTextEntered] = useState<boolean>(false);
    const [isSearch, setIsSearch] = useState<boolean>(true);

    const [step, setStep] = useState(1);
    const [headerDescription, setHeaderDescription] = useState<string>("");

    const [selectedEventPackage, setSelectedEventPackage] = useState<PackageCategory | null>(null);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const seller_id = useTypedSelector(state => state.sessionState.session?.uid) || 1;

    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const customer = useTypedSelector(state => state.bookingState.bookingProcess.customer);
    const availableItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);

    const startDate = useTypedSelector(state => state.bookingState.bookingProcess.filter.date);
    const {sellerStandardCategories, eventsSubcategories} = usePackageCategory();

    const selectedPackages = bookingProcess?.selectedPackageItems;
    const [showWarning, setShowWarning] = useState<boolean>(false);

    const initSellerCartHandler = () => {
        dispatch(BookingAction.initSellerCart(true, seller_id))
            .then(r => {
                toast.success(t("Creating new reservation"));
                dispatch(BookingAction.resetCustomer());
            })
            .catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };
    const startDateFromFilter = () => {
        let startDateFormatted = new Date(Dates.ymd(startDate));
        const today = new Date();
        if (startDateFormatted > today) {
            return startDateFormatted;
        }
        return today;
    };

    const onCompanyAutocompleteHandler = e => {
        console.log(e, e.target.value);
        const name = e.target.value;
        if (name.length === 0) {
            setTextEntered(false);
            return;
        }
        if (name.length < 2) {
            setTextEntered(false);
            return;
        }

        setTextEntered(true);
        onCompanyAutocompleteHandlerDebounce(name);
    };

    const onCompanyAutocompleteHandlerDebounce =
        useDebouncedCallback((name) =>
            dispatch(SellerAction.fetchGetCompanyFromAutocomplete(name))
                .then((foundCompanies: Company[]) => {
                    setCompanies(foundCompanies);
                }).catch(err => {
                    if (err?.status && err?.status === 401) {
                        history.replace("/booking/seller/login");
                    }
                }),
            400
        );

    const dateHandler = (date): void => {
        const newDate = Dates.ymd(date);
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                date: newDate,
            }),
        );
    };

    const datePickerLocale = declareLocaleByConfig(LOCALE);
    registerLocale("locale", datePickerLocale);
    setDefaultLocale(datePickerLocale);

    const selectCompanyHandler = (company: Company) => {
        AlertConfirmation({
            message: t("Are you sure you want to select this company?"),
            onClick: () => onSubmittedCompanyHandler(company),
            title: t("Select company"),
        });
        console.log(company);
    };

    const onSubmittedCompanyHandler = (company: Company) => {
        dispatch(BookingAction.setCustomer(company));
        if (packagesCart) {
            dispatch(BookingAction.fetchSetCustomerByCompanyId(company.id, packagesCart))
                .then(r => {})
                .catch(err => {
                    if (err?.status && err?.status === 401) {
                        history.replace("/booking/seller/login");
                    }
                });
        }
        toast.success(t("Company created"));
        setStep(2);
    };

    const selectTimeslotHandler = (timeslot: Timeslot) => {
        console.log("select timeslot successful");
        if (timeslot) {
            setStep(3);
        }
    };

    const fetchPackagesByCategoryAndTimeSlot = (packageCategory: PackageCategory) => {
        return dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                {
                    adults: bookingFilter.adults,
                    children: bookingFilter.children,
                    date: bookingFilter.date,
                    package_category_name: packageCategory,
                    hall_id: DEFAULT_HALL_ID,
                    activity_ids: bookingFilter.activity_ids.length === 0 ? [1] : bookingFilter.activity_ids,
                    timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
                },
                seller_id,
            ),
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
        }).catch(err => {
            if (err?.status && err?.status === 401) {
                history.replace("/booking/seller/login");
            }
        });
    };

    const setSelectedEventPackageHandler = (packageCategory: PackageCategory) => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                category: packageCategory,
            }),
        );

        fetchPackagesByCategoryAndTimeSlot(packageCategory)
            .then(r => {
                setSelectedEventPackage(packageCategory);
            }).catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };

    const confirmSelectedEventPackageHandler = (packageCategory: PackageCategory) => {
        fetchPackagesByCategoryAndTimeSlot(packageCategory)
            .then(r => {
                setStep(4);
                // AlertConfirmation({
                //     message: t("Please choose one of the event durations"),
                //     onClick: () => {
                //         setSelectedEventPackage(packageCategory);
                //         setStep(4);
                //     },
                //     children: <PackageCategoryItems availableItems={availableItems} />,
                //     title: t("Select event duration"),
                // });
            })
            .catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };

    // const fetchPackages = (packageCategory: PackageCategory) => {
    //     dispatch(
    //         BookingAction.fetchPackagesByCategoryAndTimeSlot(
    //             {
    //                 adults: bookingFilter.adults,
    //                 children: bookingFilter.children,
    //                 date: bookingFilter.date,
    //                 package_category_name: packageCategory,
    //                 hall_id: DEFAULT_HALL_ID,
    //                 activity_ids: bookingFilter.activity_ids.length === 0 ? [1] : bookingFilter.activity_ids,
    //                 timeslot_id: bookingProcess.selectedTimeslot ? bookingProcess.selectedTimeslot.id : null,
    //             },
    //             seller_id
    //         )
    //     ).then(r => {
    //         consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
    //     });
    // };

    const continueToFinishHandler = () => {
        console.log("im here");
        setStep(6);
    };

    const continueToUpsellsHandler = () => {
        //now its time to book
        setStep(5);
    };

    const onClickBackHandler = () => {
        setStep(step - 1);
    };

    const confirmBookingHandler = () => {
        if (packagesCart) {
            dispatch(BookingAction.confirmBookingBySeller(packagesCart, seller_id))
                .then(r => {
                    dispatch(BookingAction.clearBookingProcess());
                    props.history.push("/booking/seller/reservations/detail/" + r.packageCart.reservation_id);
                }).catch(err => {
                    if (err?.status && err?.status === 401) {
                        history.replace("/booking/seller/login");
                    }
                });
        }
    };
    const onClickNextHandler = () => {
        console.log("onClickNextHandler");
        if (step === 3) {
            setStep(4);
        }
        if (step === 4) {
            if (showWarning) {
                return AlertConfirmation({
                    message: t("Are you sure you want to book this package?"),
                    onClick: () => {
                        continueToUpsellsHandler();
                    },
                    title:
                        t("The end time of the package is after hall opening hours"),
                });
            } else {
                continueToUpsellsHandler();
            }
        }
        if (step === 5) {
            console.log("im here on step 5");
            continueToFinishHandler();
            // continueToUpsellsHandler();
        }

        if (step === 6) {
            confirmBookingHandler();
        }
    };

    useEffect(() => {
        switch (step) {
            case 1:
                setHeaderDescription(t("Search for company or create a new one"));
                break;
            case 2:
                setHeaderDescription(t("Select a date"));
                break;
            case 3:
                setHeaderDescription(t("Select number of participants"));
                break;
            case 4:
                setHeaderDescription(t("Select main package"));
                setSelectedEventPackage(null);
                break;

            case 5:
                setHeaderDescription(t("Select upsells"));
                break;
            case 6:
                setHeaderDescription(t("Confirm booking"));

                break;
            default:
                setHeaderDescription(t("Search for company or create a new one"));
                break;
        }
    }, [step]);

    // check if selected package ended after opening hours
    useEffect(() => {
        if (selectedPackages && selectedPackages?.length > 0 && availableItems?.packageItems && (availableItems?.packageItems?.adult.length > 0 || availableItems?.packageItems?.child.length > 0)) {
            const selectedPackage = selectedPackages[0];
            const fundedPackage = availableItems?.packageItems?.[selectedPackage.person_type].find(item => item.id == selectedPackage.id);

            if (fundedPackage) {
                const packageDurationInHours = fundedPackage.duration / 60;
                const timeslotDate = selectedTimeslot?.start_at ? dayjs(selectedTimeslot.start_at) : null;
                const selectedPackageHasEndedAfterOpeningHours: boolean = hallOpeningHours.packageHasEndedAfterHallOpeningHours(timeslotDate, packageDurationInHours);
                setShowWarning(selectedPackageHasEndedAfterOpeningHours);
            }
        }
    }, [selectedPackages]);

    useEffect(() => {
        initSellerCartHandler();
    }, []);

    // useEffect(() => {
    //     if (!packagesCart || !customer) {
    //         return;
    //     }
    //     if (packagesCart?.company_id === customer.id) {
    //         //already have company set
    //         setStep(2);
    //     }
    // }, []);

    return (
        <div className={"SellerNewReservation w-full"}>
            <div className={"w-full text-left"}>
                <SellerHeader step={step} description={headerDescription} />

                <div className={"steps flex flex-col"}>
                    {step === 1 && (
                        <div className={"step-1 flex flex-col justify-center items-center"}>
                            <div className={"flex flex-row"}>
                                <SellerButtonSwitch
                                    name={"isSearch"}
                                    title={t("Search company")}
                                    onClick={() => setIsSearch(true)}
                                    position={"left"}
                                    isActive={isSearch}
                                />
                                <SellerButtonSwitch
                                    name={"isNewCompany"}
                                    title={t("Create new company")}
                                    onClick={() => setIsSearch(false)}
                                    position={"right"}
                                    isActive={!isSearch}
                                />
                            </div>

                            {isSearch && (
                                <div className={"flex flex-col w-full gap-6 mt-6 justify-center items-center"}>
                                    <BookingCard className={"w-full md:max-w-lg !px-0 !py-0"}>
                                        <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3 border-b"}
                                                          title={t("Search")} />
                                        <form
                                            className={"flex flex-col align-left items-left justify-start text-left w-full px-3 py-3"}>
                                            <label
                                                className={"font-medium mb-1 flex text-sm"}>{t("Company name")}</label>
                                            <div className={"relative rounded-md shadow-sm"}>
                                                <div
                                                    className={"absolute inset-y-0 h-full left-0 pl-3 flex items-center pointer-events-none z-20"}>
                                                    <UserIcon aria-hidden="true"
                                                              className={"h-5 w-5 text-black text-opacity-30"} />
                                                </div>
                                                <input
                                                    type={"text"}
                                                    className={clsx(
                                                        "w-full py-3 mt-0 border rounded-md shadow-xs focus:outline-none active:ring-cta-800 active:ring-2 active:ring-offset-2 focus:border-cta-800 checked:bg-cta-800 sm:text-sm",
                                                        "pl-10 pr-3",
                                                        "border-gray-800",
                                                    )}
                                                    name={"company_name"}
                                                    onChange={onCompanyAutocompleteHandler}
                                                    placeholder={t("Type searched name or company")}
                                                />
                                            </div>
                                        </form>
                                    </BookingCard>

                                    {textEntered && (
                                        <BookingCard className={clsx("ReservationsForDate !px-0 !py-0 w-full")}>
                                            <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3"}
                                                              title={t("Companies / peoples")} />

                                            <Table className={"border-t"} responsive={true}>
                                                <TableHead>
                                                    <TableHeadColumn>
                                                        <TableHeadTitle>
                                                            <BuildingOfficeIcon
                                                                className={"w-4 h-4 mr-1.5 flex-none"} />
                                                            <span>{t("Name")}</span>
                                                        </TableHeadTitle>
                                                    </TableHeadColumn>
                                                    <TableHeadColumn>
                                                        <TableHeadTitle>
                                                            <UserIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                                            <span>{t("First name")}</span>
                                                        </TableHeadTitle>
                                                    </TableHeadColumn>
                                                    <TableHeadColumn>
                                                        <TableHeadTitle>
                                                            <UserIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                                            <span>{t("Last name")}</span>
                                                        </TableHeadTitle>
                                                    </TableHeadColumn>
                                                    <TableHeadColumn>
                                                        <TableHeadTitle>
                                                            <MapPinIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                                            <span>{t("Street address")}</span>
                                                        </TableHeadTitle>
                                                    </TableHeadColumn>
                                                    <TableHeadColumn>
                                                        <TableHeadTitle>
                                                            <MapIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                                            <span>{t("City")}</span>
                                                        </TableHeadTitle>
                                                    </TableHeadColumn>
                                                    <TableHeadColumn>
                                                        <TableHeadTitle>
                                                            <EnvelopeIcon className={"w-4 h-4 mr-1.5 flex-none"} />
                                                            <span>{t("Email")}</span>
                                                        </TableHeadTitle>
                                                    </TableHeadColumn>
                                                </TableHead>
                                                <TableBody>
                                                    {textEntered &&
                                                        companies.length > 0 &&
                                                        companies.map((company, index) => {
                                                            return <CompanyItem key={index} company={company}
                                                                                onClick={() => selectCompanyHandler(company)} />;
                                                        })}
                                                    {textEntered && companies?.length == 0 && (
                                                        <TableRow>
                                                            <td colSpan={6} className={"px-3 py-2 text-center"}>
                                                                {t("No companies found")}
                                                            </td>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </BookingCard>
                                    )}
                                </div>
                            )}

                            {!isSearch && (
                                <div className={"flex col-span-1 w-full mt-6 max-w-xl"}>
                                    <SellerCompanyForm onSubmitted={onSubmittedCompanyHandler} editEnabled={false}
                                                       hideReservationNotes={true} />
                                </div>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div className={"step-2 grid grid-cols-1 md:grid-cols-6 items-start gap-6 w-full"}>
                            {/*<BookingCard className={"col-span-1 md:col-span-6 lg:col-span-2 xl:col-span-2 !px-3 !pt-2 !pb-2 xl:!px-3 xl:!pt-3 xl:!pb-2"}>*/}
                            <BookingCard className={"col-span-1 md:col-span-6 lg:col-span-2 xl:col-span-2 !px-0 !py-0"}>
                                <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3 border-b"} title={"Date"} />
                                <div
                                    className={"BookingDateFilterInlineCalendarWrapper CalendarNewStyle flex-col w-full flex justify-end"}>
                                    <DatePicker
                                        dateFormat={"dd.MM.yyyy"}
                                        locale="locale"
                                        popperPlacement={"bottom-end"}
                                        renderCustomHeader={props => <DatePickerCustomHeader {...props} />}
                                        selected={startDateFromFilter()}
                                        inline
                                        onChange={dateHandler}
                                        showDisabledMonthNavigation
                                    />
                                </div>
                            </BookingCard>
                            <SellerTimeslots selectTimeslotHandler={selectTimeslotHandler}
                                             finishHandler={continueToFinishHandler} seller_id={seller_id} />
                            <ReservationsForDate className={"col-span-1 md:col-span-6"} />
                        </div>
                    )}
                    {step === 3 && (
                        <div className={"step-4 flex flex-col"}>
                            <aside className={"flex flex-col space-y-4"}>
                                <SellerPersonsCount />
                            </aside>
                        </div>
                    )}
                    {step === 4 && (
                        <div className={"step-3 flex flex-col"}>
                            <aside>
                                <ul role="list" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                    {eventsSubcategories.filter(eventPackage => !eventPackage.deprecated).map((eventPackage, index) => {
                                        return (
                                            <SellerPackageItem
                                                key={index}
                                                eventPackage={eventPackage}
                                                notSelected={selectedEventPackage ? selectedEventPackage !== eventPackage.id : null}
                                                onClick={() => confirmSelectedEventPackageHandler(eventPackage.id)}
                                                onSelect={() => setSelectedEventPackageHandler(eventPackage.id)}
                                            />
                                        );
                                    })}
                                    {sellerStandardCategories.filter(eventPackage => !eventPackage.deprecated).map((eventPackage, index) => {
                                        return (
                                            <SellerPackageItem
                                                key={index}
                                                eventPackage={eventPackage}
                                                notSelected={selectedEventPackage ? selectedEventPackage !== eventPackage.id : null}
                                                onClick={() => confirmSelectedEventPackageHandler(eventPackage.id)}
                                                onSelect={() => setSelectedEventPackageHandler(eventPackage.id)}
                                            />
                                        );
                                    })}
                                </ul>
                            </aside>
                        </div>
                    )}

                    {step === 5 && (
                        <div className={"step-5 flex flex-col gap-y-4"}>
                            <SellerUpsells backHandler={onClickBackHandler} finishHandler={onClickNextHandler}
                                           seller_id={seller_id} />
                        </div>
                    )}

                    {step === 6 && (
                        <div className={"step-6 flex flex-col gap-y-6"}>
                            <SellerBookingRecapitulation />
                        </div>
                    )}
                </div>

                {[2, 3, 4, 6].includes(step) && (
                    <div className={"flex flex-row justify-center mt-6 w-full"}>
                        <SellerFooter
                            nextStepDisabled={step == 4 && selectedEventPackage == null}
                            onClickBack={() => setStep(step - 1)}
                            onClickNext={[3, 4, 5, 6].includes(step) ? onClickNextHandler : undefined}
                            showWarning={step === 4 && showWarning}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default withRouter(SellerNewReservation);
